import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layouts/MainLayout';
import ImageGallery from '../../components/common/ImageGallery';
import Card from '../../components/common/Card';
import Badge from '../../components/common/Badge';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { productService } from '../../services/product.service';
import { useToast } from '../../components/common/Toast';
import { theme } from '../../config/theme';
import { formatProductPrice, getStockStatus, calculateProfitMargin } from '../../utils/productHelpers';
import { ArrowLeftIcon } from '../../components/icons/Icons';

const AdminProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await productService.getById(id);
      setProduct(response.data.product);
    } catch (error) {
      toast.error('Product not found');
      navigate('/admin/products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${product.name}"? This action cannot be undone.`)) return;
    
    try {
      await productService.delete(product.id);
      toast.success('Product deleted successfully');
      navigate('/admin/products');
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  const handleToggleActive = async () => {
    try {
      await productService.update(product.id, {
        ...product,
        is_active: !product.is_active,
      });
      toast.success(`Product ${!product.is_active ? 'activated' : 'deactivated'}`);
      fetchProduct();
    } catch (error) {
      toast.error('Failed to update product status');
    }
  };

  if (loading) return <LoadingSpinner fullScreen />;
  if (!product) return null;

  const stockStatus = getStockStatus(product);
  const profitMargin = calculateProfitMargin(product.price, product.cost_per_item);

  return (
    <MainLayout>
      <div style={{ padding: theme.spacing.xl, maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.lg }}>
          {/* ✅ FIXED: Properly styled back button */}
          <Button variant="ghost" onClick={() => navigate('/admin/products')}>
            <ArrowLeftIcon size={18} />
            Back to Products
          </Button>
          <div style={{ display: 'flex', gap: theme.spacing.sm }}>
            <Button variant="secondary" onClick={handleToggleActive}>
              {product.is_active ? 'Deactivate' : 'Activate'}
            </Button>
            <Button variant="danger" onClick={handleDelete}>
              Delete Product
            </Button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.xl }}>
          <div>
            <ImageGallery images={product.images} altText={product.name} />
          </div>

          <div>
            <h1 style={{ fontSize: theme.typography.fontSize['3xl'], fontWeight: theme.typography.fontWeight.bold, marginBottom: theme.spacing.md }}>
              {product.name}
            </h1>

            <div style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.lg }}>
              <Badge variant={product.is_active ? 'success' : 'error'}>
                {product.is_active ? 'Active' : 'Inactive'}
              </Badge>
              {product.is_featured && <Badge variant="primary">Featured</Badge>}
              <Badge variant={stockStatus.color === 'success' ? 'success' : stockStatus.color === 'warning' ? 'warning' : 'error'}>
                {stockStatus.label}
              </Badge>
            </div>

            {/* Seller Information */}
            {product.seller && (
              <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
                <h3 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                  Seller Information
                </h3>
                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Store Name</div>
                  <div style={{ fontWeight: theme.typography.fontWeight.medium }}>{product.seller.store_name}</div>
                </div>
              </Card>
            )}

            {/* Category */}
            {product.category && (
              <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
                <h3 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                  Category
                </h3>
                <div>{product.category.name}</div>
              </Card>
            )}

            {/* Pricing */}
            <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
              <h3 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                Pricing
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Selling Price</div>
                  <div style={{ fontSize: theme.typography.fontSize.xl, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.primary.main }}>
                    {formatProductPrice(product.price)}
                  </div>
                </div>
                {product.compare_price && (
                  <div>
                    <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Compare Price</div>
                    <div style={{ fontSize: theme.typography.fontSize.xl, textDecoration: 'line-through', color: theme.colors.text.secondary }}>
                      {formatProductPrice(product.compare_price)}
                    </div>
                  </div>
                )}
                {product.cost_per_item && (
                  <>
                    <div>
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Cost</div>
                      <div style={{ fontSize: theme.typography.fontSize.lg }}>
                        {formatProductPrice(product.cost_per_item)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Profit Margin</div>
                      <div style={{ fontSize: theme.typography.fontSize.lg, color: profitMargin > 20 ? theme.colors.success : theme.colors.warning }}>
                        {profitMargin.toFixed(1)}%
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Card>

            {/* Inventory */}
            <Card padding="lg" style={{ marginBottom: theme.spacing.lg }}>
              <h3 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                Inventory
              </h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.md }}>
                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>SKU</div>
                  <div>{product.sku || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Barcode</div>
                  <div>{product.barcode || 'N/A'}</div>
                </div>
                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Quantity</div>
                  <div>{product.track_inventory ? product.quantity : 'Not tracked'}</div>
                </div>
                <div>
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: theme.colors.text.secondary }}>Weight</div>
                  <div>{product.weight ? `${product.weight} kg` : 'N/A'}</div>
                </div>
              </div>
            </Card>

            {product.description && (
              <Card padding="lg">
                <h3 style={{ fontSize: theme.typography.fontSize.base, fontWeight: theme.typography.fontWeight.semibold, marginBottom: theme.spacing.md }}>
                  Description
                </h3>
                <p style={{ lineHeight: 1.6, color: theme.colors.text.secondary }}>
                  {product.description}
                </p>
              </Card>
            )}

            {product.tags && product.tags.length > 0 && (
              <div style={{ marginTop: theme.spacing.lg }}>
                <div style={{ fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, marginBottom: theme.spacing.sm }}>
                  Tags
                </div>
                <div style={{ display: 'flex', gap: theme.spacing.xs, flexWrap: 'wrap' }}>
                  {product.tags.map((tag, index) => (
                    <Badge key={index}>{tag}</Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default AdminProductDetails;